import { forEach } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { loginByCode, refreshMyDepositAccessToken } from "shared/api/mydeposit/auth";
import {
    MY_DEPOSIT_ACCESS_TOKEN,
    MY_DEPOSIT_REFRESH_TOKEN,
    REFRESH_TOKEN_RETRY_COUNT,
    REFRESH_TOKEN_RETRY_DELAY,
} from "shared/constants/authConstants";
import { retry } from "shared/utils/promise.utils";

interface TokenPair {
    access_token: string;
    refresh_token: string;
}

const removeTokenPairFromLocalStorage = (): void => {
    forEach([MY_DEPOSIT_REFRESH_TOKEN, MY_DEPOSIT_ACCESS_TOKEN], value => window.localStorage.removeItem(value));
};

const saveTokenPairToLocalStorage = (tokenPair: TokenPair): void => {
    window.localStorage.setItem(MY_DEPOSIT_ACCESS_TOKEN, tokenPair.access_token);
    window.localStorage.setItem(MY_DEPOSIT_REFRESH_TOKEN, tokenPair.refresh_token);
};

export class MyDepositStore {
    @observable public accessToken?: string;

    constructor() {
        makeObservable(this);
        window.addEventListener("storage", this.updateLocalStorageHandler, false);

        const accessToken = window.localStorage.getItem(MY_DEPOSIT_ACCESS_TOKEN);
        const refreshToken = window.localStorage.getItem(MY_DEPOSIT_REFRESH_TOKEN);
        if (accessToken && refreshToken) {
            this.setTokenPair({ access_token: accessToken, refresh_token: refreshToken });
        }
    }

    @computed
    public get isAuthenticated(): boolean {
        return !!this.accessToken;
    }

    @action.bound
    public async requestMyDepositAccessToken(code: string, redirectUri: string): Promise<void> {
        const response = await loginByCode(code, redirectUri);
        this.setTokenPair(response);
    }

    @action.bound
    public async refreshAccessToken(): Promise<void> {
        try {
            const response = await retry(
                () => {
                    const refreshToken = window.localStorage.getItem(MY_DEPOSIT_REFRESH_TOKEN);
                    return refreshMyDepositAccessToken(refreshToken!);
                },
                REFRESH_TOKEN_RETRY_DELAY,
                REFRESH_TOKEN_RETRY_COUNT
            );
            this.setTokenPair(response);
        } catch (error) {
            this.logout();
            throw error;
        }
    }

    @action.bound
    public logout(): void {
        this.setAccessToken(undefined);
    }

    @action.bound
    private setTokenPair(tokenPair: TokenPair): void {
        this.setAccessToken(tokenPair.access_token);
        saveTokenPairToLocalStorage(tokenPair);
    }

    @action.bound
    private updateLocalStorageHandler(storageEvent: StorageEvent): void {
        const newValue = storageEvent.newValue || undefined;
        switch (storageEvent.key) {
            case MY_DEPOSIT_ACCESS_TOKEN:
                this.setAccessToken(newValue);
                break;
            default:
                break;
        }
    }

    @action.bound
    private setAccessToken(accessToken?: string) {
        if (accessToken) {
            this.accessToken = accessToken;
        } else {
            this.accessToken = undefined;
            removeTokenPairFromLocalStorage();
        }
    }
}
